import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Archive,
  Bot,
  CalendarDays,
  CornerLeftUp,
  ListTree,
  MoreHorizontal,
  Pencil,
  Plus,
  UserRound,
} from "lucide-react";
import { type CSSProperties, type MouseEvent, type KeyboardEvent, memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TaskForm } from "@/components/kanban/TaskForm";
import type {
  Tag,
  Task,
  TaskUpdateRequest,
  TenantUser,
} from "@/lib/project-types";
import {
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABEL_KEYS,
  TASK_STATUS_SHAPE_CLASSES,
} from "@/lib/task-status";
import { cn } from "@/lib/utils";
import { formatTaskDueDate, isCalendarDate, isTaskOverdue, todayInTimezone } from "@/lib/task-due-date";

const RELATIVE_TIME_INTERVALS: Array<{
  unit: Intl.RelativeTimeFormatUnit;
  milliseconds: number;
}> = [
  { unit: "year", milliseconds: 365 * 24 * 60 * 60 * 1000 },
  { unit: "month", milliseconds: 30 * 24 * 60 * 60 * 1000 },
  { unit: "week", milliseconds: 7 * 24 * 60 * 60 * 1000 },
  { unit: "day", milliseconds: 24 * 60 * 60 * 1000 },
  { unit: "hour", milliseconds: 60 * 60 * 1000 },
  { unit: "minute", milliseconds: 60 * 1000 },
];

const TAG_COLOR_OPTIONS = [
  "gray",
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
] as const;

type TagColor = (typeof TAG_COLOR_OPTIONS)[number];

const TAG_COLOR_CLASSES: Record<TagColor, string> = {
  gray: "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200",
  red: "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200",
  orange: "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/60 dark:text-orange-200",
  yellow: "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-900 dark:bg-yellow-950/60 dark:text-yellow-200",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/60 dark:text-blue-200",
  purple: "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/60 dark:text-violet-200",
  pink: "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-900 dark:bg-pink-950/60 dark:text-pink-200",
};

const TAG_RAIL_CLASSES: Record<TagColor, string> = {
  gray: "border-slate-600 dark:border-slate-400",
  red: "border-red-600 dark:border-red-400",
  orange: "border-orange-600 dark:border-orange-400",
  yellow: "border-yellow-600 dark:border-yellow-400",
  green: "border-emerald-600 dark:border-emerald-400",
  blue: "border-blue-600 dark:border-blue-400",
  purple: "border-violet-600 dark:border-violet-400",
  pink: "border-pink-600 dark:border-pink-400",
};

export function tagColorClass(color?: string | null): string {
  return color && color in TAG_COLOR_CLASSES
    ? TAG_COLOR_CLASSES[color as TagColor]
    : "border-border bg-muted text-muted-foreground";
}

export function tagRailClass(color?: string | null): string {
  return color && color in TAG_RAIL_CLASSES
    ? TAG_RAIL_CLASSES[color as TagColor]
    : "border-border";
}

export function tagColorStyle(color?: string | null): CSSProperties | undefined {
  return color && /^#[0-9a-f]{3,8}$/i.test(color)
    ? { borderColor: color, color }
    : undefined;
}

/** A compact, locale-aware timestamp used consistently on task cards. */
export function formatRelativeTaskTime(value: string, locale?: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "";

  const elapsed = Math.max(0, Date.now() - timestamp);
  const formatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "short",
  });
  const interval = RELATIVE_TIME_INTERVALS.find(
    (item) => elapsed >= item.milliseconds,
  );

  if (!interval) return formatter.format(0, "second");
  return formatter.format(
    -Math.floor(elapsed / interval.milliseconds),
    interval.unit,
  );
}

export interface KanbanCardProps {
  task: Task;
  /** Optional override for the task's resolved tags, useful for card previews. */
  tags?: Tag[];
  /** Every tag returned for this project, including assigned archived tags. */
  allTags?: Tag[];
  /** Resolved session agent, if available. */
  agentId?: string;
  agentName?: string;
  defaultAgentId?: string | null;
  active?: boolean;
  selected?: boolean;
  onSelectionClick?: (task: Task, event: MouseEvent | KeyboardEvent) => boolean;
  onClick: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask?: (task: Task) => void;
  hierarchySupported?: boolean;
  onUpdateTask?: (
    taskId: string,
    updates: TaskUpdateRequest,
  ) => Promise<boolean>;
  onUpdateTags?: (taskId: string, tagIds: string[]) => Promise<boolean>;
  onCreateTag?: (name: string, color?: string) => Promise<Tag | null>;
  onArchiveTag?: (tagId: string) => Promise<boolean>;
  onRestoreTag?: (tagId: string) => Promise<boolean>;
  tenantUsers?: TenantUser[];
  dueDateSupported?: boolean;
  timezone?: string;
}

export interface KanbanCardContentProps extends KanbanCardProps {
  overlay?: boolean;
}

function tenantUserLabel(user: TenantUser): string {
  return user.display_name ?? user.email ?? user.id;
}

/** Presentation-only card; safe to render inside the root DragOverlay. */
export const KanbanCardContent = memo(function KanbanCardContent({
  task,
  agentId,
  agentName,
  defaultAgentId,
  active = false,
  selected = false,
  onSelectionClick,
  onClick,
  onDeleteTask,
  onAddSubtask,
  hierarchySupported = false,
  onUpdateTask,
  onCreateTag,
  tenantUsers = [],
  dueDateSupported = false,
  timezone = "Europe/Zurich",
  tags,
  allTags = [],
  overlay = false,
}: KanbanCardContentProps) {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const relativeTime = formatRelativeTaskTime(task.updated_at, i18n.language);
  const dueDate = dueDateSupported && isCalendarDate(task.due_date) ? task.due_date : null;
  const overdue = isTaskOverdue(dueDate, task.status, todayInTimezone(timezone));
  const visibleAgentName = agentId && agentId !== defaultAgentId ? agentName ?? agentId : undefined;
  const statusLabel = t(TASK_STATUS_LABEL_KEYS[task.status]);
  const [editing, setEditing] = useState(false);
  const assignee = task.assigned_to
    ? tenantUsers.find((user) => user.id === task.assigned_to)
    : undefined;
  const assigneeLabel = assignee ? tenantUserLabel(assignee) : task.assigned_to;
  const allTagsById = useMemo(
    () => new Map(allTags.map((tag) => [tag.id, tag])),
    [allTags],
  );
  const assignedTags = useMemo(
    () => (tags ?? task.tags ?? []).map((tag) => allTagsById.get(tag.id) ?? tag),
    [allTagsById, tags, task.tags],
  );
  const displayTags = task.effective_tags
    ? Array.from(task.effective_tags.reduce((map, entry) => {
      if (!map.has(entry.tag.id) || !entry.inherited) map.set(entry.tag.id, entry);
      return map;
    }, new Map<string, (typeof task.effective_tags)[number]>()).values())
    : assignedTags.map((tag) => ({ tag, inherited: false, source_task: null }));
  const ancestorPath = task.parent_task_id
    && task.ancestor_path?.at(-1)?.id === task.parent_task_id
    && task.ancestor_path.every((ancestor) => Boolean(ancestor.id && ancestor.title))
    && new Set(task.ancestor_path.map((ancestor) => ancestor.id)).size === task.ancestor_path.length
      ? task.ancestor_path
      : [];
  const parent = ancestorPath.at(-1);
  const directSubtaskCount = hierarchySupported && Number.isInteger(task.direct_subtask_count) && task.direct_subtask_count! > 0
    ? task.direct_subtask_count
    : null;
  const taskHref = (id: string) => `/project/${encodeURIComponent(task.project_id)}?task=${encodeURIComponent(id)}`;
  if (editing && !overlay && onUpdateTask) {
    return (
      <TaskForm
        mode="edit"
        status={task.status}
        task={task}
        tenantUsers={tenantUsers}
        dueDateSupported={dueDateSupported}
        allTags={allTags}
        onCreateTag={onCreateTag}
        onUpdateTask={onUpdateTask}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );
  }

  return (
    <Card asChild>
      <article
        className={cn(
          "group relative min-w-0 p-3 transition-shadow",
          "cursor-grab hover:shadow-md active:cursor-grabbing",
          active && "border-primary bg-primary/5 ring-1 ring-primary/25",
          selected && "border-primary bg-primary/10 ring-2 ring-primary/50",
          overlay && "w-72 cursor-grabbing shadow-xl",
        )}
      >
      <div
        className="min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        role="button"
        aria-pressed={selected}
        aria-label={selected ? t("projects:bulkSelectedCard", { title: task.title }) : undefined}
        tabIndex={overlay ? -1 : 0}
        data-testid={`kanban-card-content-${task.id}`}
        onClick={(event) => {
          if (!overlay && !onSelectionClick?.(task, event)) onClick(task);
        }}
        onKeyDown={(event) => {
          if (overlay || event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!onSelectionClick?.(task, event)) onClick(task);
          }
        }}
      >
        <div className="flex min-w-0 items-start gap-2 pr-7">
          <span
            className={cn(
              "mt-1.5 size-2 shrink-0 rounded-full",
              TASK_STATUS_DOT_CLASSES[task.status],
              TASK_STATUS_SHAPE_CLASSES[task.status],
            )}
            aria-label={statusLabel}
            title={statusLabel}
          />
          <p className="min-w-0 flex-1 break-words text-sm font-medium leading-5">{task.title}</p>
        </div>
        {(task.parent_task_id || directSubtaskCount !== null) && (
          <div className="mt-1 ml-4 flex min-w-0 items-center gap-1">
            {task.parent_task_id && (parent && !overlay ? (
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0 text-muted-foreground"
                aria-label={t("projects:subtasks.openParentTask", { title: parent.title })}
                title={t("projects:subtasks.openParentTask", { title: parent.title })}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => { event.stopPropagation(); navigate(taskHref(parent.id)); }}>
                <CornerLeftUp className="size-3.5" aria-hidden="true" />
              </Button>
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground"
                role="img" aria-label={parent ? t("projects:subtasks.childOf", { title: parent.title }) : t("projects:subtasks.parentUnavailable")}
                title={parent ? t("projects:subtasks.childOf", { title: parent.title }) : t("projects:subtasks.parentUnavailable")}>
                <CornerLeftUp className="size-3.5" aria-hidden="true" />
              </span>
            ))}
            {directSubtaskCount !== null && (overlay ? (
              <span className="flex h-7 items-center gap-1 px-1.5 text-muted-foreground"
                aria-label={t("projects:subtasks.cardDirectCount", { count: directSubtaskCount })}>
                <ListTree className="size-4" aria-hidden="true" />
                <span className="text-xs tabular-nums" aria-hidden="true">{directSubtaskCount}</span>
              </span>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 gap-1 px-1.5 text-muted-foreground"
                aria-label={t("projects:subtasks.openCardSubtasks", { count: directSubtaskCount })}
                title={t("projects:subtasks.openCardSubtasks", { count: directSubtaskCount })}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => { event.stopPropagation(); navigate(taskHref(task.id)); }}>
                <ListTree className="size-4" aria-hidden="true" />
                <span className="text-xs tabular-nums" aria-hidden="true">{directSubtaskCount}</span>
              </Button>
            ))}
          </div>
        )}
        {displayTags.length > 0 && (
          <div
            className="mt-2 flex min-w-0 flex-wrap items-center gap-1"
          >
            {displayTags.map(({ tag, inherited, source_task }) => (
              <Badge
                key={tag.id}
                variant="outline"
                className={cn(
                  "max-w-full shrink-0 gap-1 px-1.5 py-0 text-[11px] font-medium leading-5",
                  inherited ? "border-border bg-transparent text-muted-foreground" : tagColorClass(tag.color),
                  tag.is_archived && "opacity-60",
                )}
                style={inherited ? undefined : tagColorStyle(tag.color)}
                title={inherited ? t("projects:subtasks.inheritedTagSource", { title: source_task?.title ?? t("projects:subtasks.parentUnavailable") }) : tag.is_archived
                  ? `${tag.name} (${t("projects:archivedTag")})`
                  : tag.name}
              >
                <span className="min-w-0 truncate">{tag.name}</span>
                {tag.is_archived && (
                  <Archive className="size-2.5 shrink-0" aria-hidden="true" />
                )}
              </Badge>
            ))}
          </div>
        )}
        {(visibleAgentName || assigneeLabel || dueDate || relativeTime) && (
          <div className="mt-2 min-w-0 space-y-1.5 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            {(visibleAgentName || assigneeLabel) && <div className="flex min-w-0 items-center gap-2">
              {visibleAgentName && (
                <span className="flex min-w-0 flex-1 items-center gap-1" title={visibleAgentName}>
                  <Bot className="size-3 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 truncate">{visibleAgentName}</span>
                </span>
              )}
              {assigneeLabel && <span className="flex min-w-0 flex-1 items-center gap-1" title={assigneeLabel}>
                <UserRound className="size-3 shrink-0" aria-hidden="true" />
                <span className="min-w-0 truncate">{assigneeLabel}</span>
              </span>}
            </div>}
            {(dueDate || relativeTime) && <div className="flex min-w-0 items-center justify-between gap-2">
              {dueDate && (
                <time dateTime={dueDate} className={cn("inline-flex min-w-0 items-center gap-1 rounded border px-1.5 py-0.5", overdue ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-border bg-muted text-foreground")}>
                  <CalendarDays className="size-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">{t(overdue ? "projects:overdueDate" : "projects:dueDateLabel", { date: formatTaskDueDate(dueDate, i18n.language) })}</span>
                </time>
              )}
              {relativeTime && (
                <time className="ml-auto shrink-0 tabular-nums" dateTime={task.updated_at}>
                  {relativeTime}
                </time>
              )}
            </div>}
          </div>
        )}
      </div>

      {!overlay && (
        <div className="absolute right-2 top-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t("projects:taskActions", { title: task.title })}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hierarchySupported && task.status !== "cancelled" && onAddSubtask && <DropdownMenuItem onSelect={() => onAddSubtask(task)}><Plus className="size-4" />{t("projects:subtasks.add")}</DropdownMenuItem>}
              <DropdownMenuItem
                disabled={!onUpdateTask}
                onSelect={() => {
                  // Let the dropdown close before mounting the auto-focused form.
                  setTimeout(() => setEditing(true), 0);
                }}
              >
                <Pencil className="size-4" />
                {t("common:actions.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onDeleteTask(task.id)}
              >
                <Archive className="size-4" />
                {t("projects:archiveTask")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      </article>
    </Card>
  );
});

/** Mounted draggable shell kept separate from the presentation-only card. */
export const DraggableKanbanCard = memo(function DraggableKanbanCard(
  props: KanbanCardProps,
) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: props.task.id,
    data: { type: "kanban-task", task: props.task },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(
        "touch-pan-y",
        isDragging && "z-10 opacity-45",
      )}
      data-testid={`kanban-card-${props.task.id}`}
      data-task-id={props.task.id}
      {...attributes}
      {...listeners}
    >
      <KanbanCardContent {...props} />
    </div>
  );
});

/** Backward-compatible name for callers that still mount a draggable card. */
export function KanbanCard(props: KanbanCardProps) {
  return <DraggableKanbanCard {...props} />;
}
